/**
 * LanguageSwitcher
 *
 * Premium language selection control.
 * - Options populated from the language registry — zero hardcoding.
 * - variant="default"  → globe + full native language name (public pages)
 * - variant="compact"  → globe + language code (navbar)
 * - RTL-aware: dropdown uses logical CSS (inset-inline-end) for correct placement in Arabic.
 *
 * Positioning note:
 *   The outer wrapper receives `className` (e.g. "absolute top-4 right-4 z-20").
 *   The inner wrapper holds `position: relative` as the dropdown's anchor.
 *   Keeping these in separate elements avoids the CSS cascade conflict that arises
 *   when "absolute" and "relative" are both on the same element.
 */
import React, { useEffect, useRef, useState } from 'react';
import i18n from '../../i18n/i18n';
import { useLanguage } from '../../hooks/useLanguage';

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact';
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'default',
  className = '',
}) => {
  const { currentLang, currentLangDef, availableLanguages, changeLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isCompact = variant === 'compact';
  const displayLabel = isCompact
    ? currentLang.toUpperCase()
    : currentLangDef?.name ?? currentLang.toUpperCase();

  return (
    // Outer div: receives caller positioning (e.g. absolute top-4 right-4 z-20)
    <div className={className} ref={ref}>
      {/* Inner div: positioning anchor for the dropdown — kept separate to avoid
          cascade conflict between "absolute" and "relative" on the same element */}
      <div className="relative inline-block">
        {/* Trigger pill */}
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          className={[
            'inline-flex items-center gap-1.5 rounded-full',
            'border border-gray-200 bg-white shadow-sm',
            'transition-all duration-200',
            'hover:border-purple-300 hover:shadow-md',
            'focus:outline-none focus:ring-2 focus:ring-purple-200 focus:ring-offset-1',
            isCompact ? 'px-2.5 py-1.5' : 'px-3.5 py-2',
          ].join(' ')}
          aria-label={i18n.t('g.select_language', 'Select language')}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <i className={`pi pi-globe text-project-purple ${isCompact ? 'text-xs' : 'text-sm'}`} />
          <span
            className={`font-medium text-gray-800 leading-none ${
              isCompact ? 'text-xs' : 'text-sm'
            }`}
          >
            {displayLabel}
          </span>
          <i
            className={[
              'pi pi-chevron-down text-gray-500 transition-transform duration-200',
              open ? 'rotate-180' : '',
              isCompact ? 'text-[10px]' : 'text-xs',
            ].join(' ')}
          />
        </button>

        {/* Dropdown panel
            inset-inline-end: 0  →  right-0 in LTR, left-0 in RTL (correct in both cases) */}
        {open && (
          <div
            className="absolute right-0 mt-2 min-w-[11rem] bg-white border border-gray-200 rounded-xl shadow-2xl z-[300] overflow-hidden py-1"
            role="listbox"
            aria-label={i18n.t('g.available_languages', 'Available languages')}
          >
            {availableLanguages.map((lang) => {
              const isSelected = lang.code === currentLang;
              return (
                <button
                  key={lang.code}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    void changeLang(lang.code);
                    setOpen(false);
                  }}
                  className={[
                    'w-full flex items-center justify-between px-4 py-3 text-sm',
                    'transition-colors duration-150 cursor-pointer text-start',
                    isSelected ? 'bg-purple-50 font-semibold' : 'hover:bg-gray-50',
                  ].join(' ')}
                  style={{ color: isSelected ? '#6C4E9A' : '#374151' }}
                >
                  <span>{lang.name}</span>
                  {isSelected && (
                    <i
                      className="pi pi-check text-xs flex-shrink-0 ms-3"
                      style={{ color: '#6C4E9A' }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
